import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BarChart3,
  BriefcaseBusiness,
  ChevronRight,
  Clock3,
  Lightbulb,
  Network,
  RefreshCw,
  UsersRound,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import AccessibleDialog from '../../components/common/AccessibleDialog';
import AccessibleChartSummary from '../../components/reports/AccessibleChartSummary';
import { getWorkforceIntelligence } from '../../services/workforceIntelligenceService';
import type { WorkforceIntelligenceDashboard } from '../../types/workforceIntelligence';
import { useTheme } from '../../context/ThemeContext';

function MetricCard({
  label,
  value,
  detail,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  detail: string;
  icon: typeof UsersRound;
}) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-500">{label}</p>
          <p className="mt-2 text-3xl font-bold text-slate-950">{value}</p>
          <p className="mt-1 text-xs text-slate-500">{detail}</p>
        </div>
        <span className="rounded-xl bg-blue-50 p-3 text-brand-primary">
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </article>
  );
}

function Distribution({
  items,
}: {
  items: { label: string; value: number; detail?: string }[];
}) {
  const max = Math.max(1, ...items.map((x) => x.value));
  if (items.length === 0)
    return (
      <p className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">
        No workforce data is currently available for this metric.
      </p>
    );
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.label}>
          <div className="mb-1 flex items-center justify-between gap-3 text-sm">
            <span
              className="truncate font-semibold text-slate-700"
              title={item.label}
            >
              {item.label}
            </span>
            <span className="shrink-0 text-slate-500">
              {item.value}
              {item.detail ? ` · ${item.detail}` : ''}
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-brand-primary"
              style={{ width: `${Math.max(2, (item.value * 100) / max)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function Panel({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      className="rounded-xl border border-slate-200 p-5 shadow-sm"
      style={{ backgroundColor: 'var(--color-surface)' }}
    >
      <h2 className="mb-4 text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>{title}</h2>
      {children}
    </section>
  );
}

type CapabilityTab = 'specialties' | 'skills' | 'experience' | 'capacity';
type CapacityMetric = 'hours' | 'employees';
const CAPABILITY_COLORS = [
  '#2563eb',
  '#0891b2',
  '#0d9488',
  '#16a34a',
  '#7c3aed',
  '#4f46e5',
  '#0284c7',
  '#059669',
  '#8b5cf6',
  '#0369a1',
];

function coverageLabel(value: string) {
  if (value.toLowerCase().includes('healthy')) return 'Broad Coverage';
  if (value.toLowerCase().includes('moderate')) return 'Moderate Coverage';
  return 'Limited Coverage';
}

function WorkforceCapabilityOverview({
  data,
}: {
  data: WorkforceIntelligenceDashboard;
}) {
  const [tab, setTab] = useState<CapabilityTab>('specialties');
  const [capacityMetric, setCapacityMetric] = useState<CapacityMetric>('hours');
  const [showAll, setShowAll] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState<{
    label: string;
    tooltip: string[];
    left: number;
    top: number;
  } | null>(null);
  const specialties = [...data.specialties].sort(
    (a, b) => b.employeeCount - a.employeeCount
  );
  const skills = [...data.skills].sort(
    (a, b) => b.employeeCount - a.employeeCount
  );
  const capacity = [...data.specialties].sort((a, b) =>
    capacityMetric === 'hours'
      ? b.availableHours - a.availableHours
      : b.availableEmployees - a.availableEmployees
  );
  const topSpecialty = specialties[0];
  const topSkill = skills[0];
  const topCapacity = [...data.specialties].sort(
    (a, b) => b.availableHours - a.availableHours
  )[0];
  const allRows =
    tab === 'specialties'
      ? specialties.map((x) => ({
          label: x.specialty,
          value: x.employeeCount,
          valueLabel: `${x.employeeCount} employees`,
          tooltip: [
            `${x.employeeCount} Employees`,
            `${x.percentage}% of Workforce`,
            `${x.availableEmployees} Currently Available`,
            `${x.availableHours} Approved hrs/week`,
            `Average Experience: ${x.averageExperience} Years`,
          ],
        }))
      : tab === 'skills'
        ? skills.map((x) => ({
            label: x.skill,
            value: x.employeeCount,
            valueLabel: `${x.employeeCount} employees`,
            tooltip: [
              `${x.employeeCount} Employees With Skill`,
              `${x.percentage}% of Workforce`,
              `${x.availableEmployees} Available`,
              `${x.availableHours} hrs/week capacity`,
              coverageLabel(x.coverageStatus),
            ],
          }))
        : tab === 'capacity'
          ? capacity.map((x) => ({
              label: x.specialty,
              value:
                capacityMetric === 'hours'
                  ? x.availableHours
                  : x.availableEmployees,
              valueLabel:
                capacityMetric === 'hours'
                  ? `${x.availableHours} hrs/week`
                  : `${x.availableEmployees} available`,
              tooltip: [
                `${x.availableHours} Approved hrs/week`,
                `${x.availableEmployees} Available Employees`,
                `${x.availableEmployees ? Math.round((x.availableHours / x.availableEmployees) * 10) / 10 : 0} Average hrs/available employee`,
                `${x.employeeCount} Total Employees`,
              ],
            }))
          : [];
  const limit = tab === 'skills' ? 10 : 7;
  const rows = allRows.slice(0, limit);
  const max = Math.max(1, ...rows.map((x) => x.value));
  const showRowTooltip = (
    event: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement>,
    row: (typeof rows)[number]
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = Math.min(256, window.innerWidth - 32);
    const left = Math.min(
      window.innerWidth - tooltipWidth - 16,
      Math.max(16, rect.left + rect.width / 2 - tooltipWidth / 2)
    );

    setActiveTooltip({
      label: row.label,
      tooltip: row.tooltip,
      left,
      top: Math.min(window.innerHeight - 152, Math.max(12, rect.top - 140)),
    });
  };
  const tabs: { id: CapabilityTab; label: string }[] = [
    { id: 'specialties', label: 'Specialties' },
    { id: 'skills', label: 'Skills' },
    { id: 'experience', label: 'Experience Distribution' },
    { id: 'capacity', label: 'Availability & Capacity' },
  ];
  const experienceTotal = data.experience.reduce(
    (sum, item) => sum + item.employeeCount,
    0
  );
  const emptyMessage =
    tab === 'specialties'
      ? 'No specialty data available.'
      : tab === 'skills'
        ? 'No skill data available.'
        : 'No approved availability capacity available.';
  const activateTab = (nextTab: CapabilityTab) => {
    setTab(nextTab);
    setShowAll(false);
  };
  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const currentIndex = tabs.findIndex((item) => item.id === tab);
    let nextIndex: number | null = null;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % tabs.length;
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = tabs.length - 1;
    }

    if (nextIndex === null) return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    activateTab(nextTab.id);
    const tabButtons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabButtons?.[nextIndex]?.focus();
  };

  return (
    <section
      className="flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm md:h-[520px]"
      aria-labelledby="capability-overview-title"
    >
      <div className="shrink-0 border-b border-slate-200 p-4">
        <h2
          id="capability-overview-title"
          className="text-xl font-bold"
          style={{ color: 'var(--color-text-primary)' }}
        >
          Workforce Capability Overview
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          A consolidated view of workforce depth and approved effective
          capacity.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          <article className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Top Specialty
            </p>
            <p
              className="mt-1 truncate font-bold text-slate-950"
              title={topSpecialty?.specialty}
            >
              {topSpecialty?.specialty ?? 'No data'}
            </p>
            <p className="text-sm text-brand-primary">
              {topSpecialty ? `${topSpecialty.employeeCount} Employees` : '—'}
            </p>
          </article>
          <article className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Top Skill
            </p>
            <p
              className="mt-1 truncate font-bold text-slate-950"
              title={topSkill?.skill}
            >
              {topSkill?.skill ?? 'No data'}
            </p>
            <p className="text-sm text-brand-primary">
              {topSkill ? `${topSkill.employeeCount} Employees` : '—'}
            </p>
          </article>
          <article className="rounded-xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Highest Available Capacity
            </p>
            <p
              className="mt-1 truncate font-bold text-slate-950"
              title={topCapacity?.specialty}
            >
              {topCapacity?.specialty ?? 'No data'}
            </p>
            <p className="text-sm text-brand-primary">
              {topCapacity ? `${topCapacity.availableHours} hrs/week` : '—'}
            </p>
          </article>
        </div>
      </div>
      <div className="flex min-h-0 flex-1 flex-col p-4">
        <div className="flex flex-col justify-between gap-2 border-b border-slate-200 sm:flex-row sm:items-end">
          <div
            role="tablist"
            aria-label="Workforce capability metric"
            className="flex max-w-full gap-1 overflow-x-auto"
          >
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                aria-controls="capability-overview-panel"
                tabIndex={tab === item.id ? 0 : -1}
                onClick={() => activateTab(item.id)}
                onKeyDown={handleTabKeyDown}
                className={`whitespace-nowrap border-b-2 px-3 py-2 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary ${tab === item.id ? 'border-brand-primary text-brand-primary' : 'border-transparent text-slate-500 hover:text-slate-900'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
          {tab === 'capacity' && (
            <div className="mb-1 inline-flex self-start rounded-lg bg-slate-100 p-1 text-xs font-bold">
              <button
                type="button"
                onClick={() => setCapacityMetric('hours')}
                className={`rounded-md px-3 py-1 ${capacityMetric === 'hours' ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500'}`}
              >
                Capacity Hours
              </button>
              <button
                type="button"
                onClick={() => setCapacityMetric('employees')}
                className={`rounded-md px-3 py-1 ${capacityMetric === 'employees' ? 'bg-white text-brand-primary shadow-sm' : 'text-slate-500'}`}
              >
                Available Employees
              </button>
            </div>
          )}
        </div>
        <div
          id="capability-overview-panel"
          role="tabpanel"
          className="mt-3 min-h-[250px] flex-1 transition-opacity duration-200"
        >
          {tab === 'experience' ? (
            experienceTotal > 0 ? (
              <div className="grid h-full items-center gap-4 md:grid-cols-[minmax(240px,0.85fr)_1.15fr]">
                <div className="relative mx-auto h-52 w-full max-w-xs">
                  <div aria-hidden="true">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.experience}
                          dataKey="employeeCount"
                          nameKey="band"
                          innerRadius={62}
                          outerRadius={92}
                          paddingAngle={2}
                          stroke="none"
                        >
                          {data.experience.map((item, index) => (
                            <Cell
                              key={item.band}
                              fill={CAPABILITY_COLORS[index]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value, _name, item) => [
                            `${Number(value)} Employees · ${item.payload.percentage}% of Workforce`,
                            item.payload.band,
                          ]}
                          contentStyle={{
                            borderRadius: 12,
                            borderColor: '#e2e8f0',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <strong className="text-2xl text-slate-950">
                      {experienceTotal}
                    </strong>
                    <span className="text-xs font-semibold text-slate-500">
                      Employees
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  {data.experience.map((item, index) => (
                    <div
                      key={item.band}
                      className="grid grid-cols-[1fr_auto_auto] items-center gap-3 rounded-lg px-3 py-2 hover:bg-white"
                    >
                      <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-slate-700">
                        <span
                          className="h-3 w-3 shrink-0 rounded-full"
                          style={{ backgroundColor: CAPABILITY_COLORS[index] }}
                        />
                        <span className="truncate">{item.band}</span>
                      </span>
                      <span className="text-sm font-bold text-slate-800">
                        {item.employeeCount}
                      </span>
                      <span className="w-12 text-right text-sm text-slate-500">
                        {item.percentage}%
                      </span>
                    </div>
                  ))}
                </div>
                <AccessibleChartSummary
                  caption="Employee experience distribution"
                  categoryHeader="Experience band"
                  valueHeader="Employees and share of workforce"
                  rows={data.experience.map((item) => ({
                    label: item.band,
                    value: `${item.employeeCount} employees (${item.percentage}%)`,
                  }))}
                />
              </div>
            ) : (
              <p className="rounded-xl bg-white p-10 text-center text-sm text-slate-500">
                No employee experience data available.
              </p>
            )
          ) : rows.length ? (
            <>
              <p className="mb-1 text-right text-xs font-medium text-slate-500 sm:hidden">
                Swipe horizontally to view all metrics →
              </p>
              <div className="relative">
                <div
                  className="overflow-x-auto pb-2"
                  onScroll={() => setActiveTooltip(null)}
                >
                  <div className="min-w-[34rem] space-y-2.5">
                {rows.map((row, index) => (
                  <div
                    key={row.label}
                    className="grid grid-cols-[11rem_1fr_8rem] items-center gap-3"
                  >
                    <span
                      className="truncate text-sm font-semibold text-slate-700"
                      title={row.label}
                    >
                      {row.label}
                    </span>
                    <button
                      type="button"
                      aria-label={`${row.label}: ${row.tooltip.join(', ')}`}
                      className="h-6 rounded-full bg-slate-100 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
                      onMouseEnter={(event) => showRowTooltip(event, row)}
                      onMouseLeave={() => setActiveTooltip(null)}
                      onFocus={(event) => showRowTooltip(event, row)}
                      onBlur={() => setActiveTooltip(null)}
                    >
                      <span
                        className="block h-full rounded-full shadow-sm transition-[width,filter] duration-500 hover:brightness-110"
                        style={{
                          width: `${Math.max(2, (row.value * 100) / max)}%`,
                          backgroundColor:
                            CAPABILITY_COLORS[index % CAPABILITY_COLORS.length],
                        }}
                      />
                    </button>
                    <span className="text-right text-sm font-semibold text-slate-600">
                      {row.valueLabel}
                    </span>
                  </div>
                ))}
                  </div>
                </div>
                <span className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-slate-50 to-transparent sm:hidden" />
              </div>
              {activeTooltip && (
                <div
                  role="tooltip"
                  className="pointer-events-none fixed z-50 w-[min(16rem,calc(100vw-2rem))] rounded-lg bg-slate-950 p-3 text-left text-xs leading-5 text-white shadow-xl"
                  style={{ left: activeTooltip.left, top: activeTooltip.top }}
                >
                  <strong className="block text-sm">{activeTooltip.label}</strong>
                  {activeTooltip.tooltip.map((line) => (
                    <span key={line} className="block">
                      {line}
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="rounded-xl bg-white p-10 text-center text-sm text-slate-500">
              {emptyMessage}
            </p>
          )}
        </div>
        {tab !== 'experience' && allRows.length > limit && !showAll && (
          <div className="mt-auto text-right">
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="rounded-md px-2 py-1 text-sm font-bold text-brand-primary hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            >
              View All →
            </button>
          </div>
        )}
      </div>
      {showAll && (
        <AccessibleDialog
          isOpen={showAll}
          onClose={() => setShowAll(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          ariaLabel={`All ${tabs.find((x) => x.id === tab)?.label}`}
        >
          <div className="max-h-[85vh] w-full max-w-5xl overflow-auto rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-slate-950">
                All {tabs.find((x) => x.id === tab)?.label}
              </h3>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                aria-label="Close detailed capability view"
                data-dialog-initial-focus
                className="rounded-lg p-2 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowAll(false)}
              className="sr-only"
            >
              Close
            </button>
            <div className="space-y-2">
              {allRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-4 rounded-lg border border-slate-100 p-3"
                >
                  <span className="font-semibold text-slate-800">
                    {row.label}
                  </span>
                  <span className="shrink-0 text-sm text-slate-600">
                    {row.valueLabel}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </AccessibleDialog>
      )}
    </section>
  );
}

function TrendBadge({
  direction,
  percentage,
  current,
  previous,
}: {
  direction: string;
  percentage: number | null;
  current: number;
  previous: number;
}) {
  const label =
    direction === 'New'
      ? 'NEW this period'
      : direction === 'Stable'
        ? '→ Stable'
        : `${direction === 'Down' ? '↓' : '↑'} ${Math.abs(percentage ?? 0)}% vs previous period`;
  const style =
    direction === 'Down'
      ? 'bg-rose-50 text-rose-700'
      : direction === 'Stable'
        ? 'bg-slate-100 text-slate-600'
        : direction === 'New'
          ? 'bg-violet-50 text-violet-700'
          : 'bg-emerald-50 text-emerald-700';
  return (
    <span
      title={`Current period: ${current}; Previous period: ${previous}; Change: ${percentage == null ? 'New' : `${percentage}%`}`}
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ${style}`}
    >
      {label}
    </span>
  );
}

function TrendingTopics({
  data,
  onFindExperts,
  onViewPost,
  onViewGroup,
}: {
  data: WorkforceIntelligenceDashboard;
  onFindExperts: (topic: string) => void;
  onViewPost: (postId: number) => void;
  onViewGroup: (groupId: number) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<
    WorkforceIntelligenceDashboard['discussionTopics'][number] | null
  >(null);
  const topics = data.discussionTopics.filter(
    (item) =>
      !['other', 'unclassified'].includes(item.topic.trim().toLowerCase())
  );
  const statusOf = (topic: (typeof topics)[number]) =>
    topic.trendDirection === 'New'
      ? 'Emerging'
      : topic.trendDirection === 'Down'
        ? 'Declining'
        : topic.trendDirection === 'Stable'
          ? 'Stable'
          : 'Trending';
  const trendText = (topic: (typeof topics)[number]) =>
    topic.trendDirection === 'New'
      ? 'NEW'
      : topic.trendDirection === 'Stable'
        ? '→ Stable'
        : `${topic.trendDirection === 'Down' ? '↓' : '↑'} ${topic.trendPercentage && topic.trendPercentage > 0 ? '+' : ''}${topic.trendPercentage ?? 0}%`;
  const renderRow = (topic: (typeof topics)[number], index: number) => (
    <button
      key={topic.topic}
      type="button"
      onClick={() => {
        setSelected(topic);
        setShowAll(false);
      }}
      className="group grid w-full grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-3 border-b border-slate-100 px-2 py-3.5 text-left transition-colors last:border-0 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-primary"
    >
      <span className="font-bold text-brand-primary">#{index + 1}</span>
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <strong className="truncate text-slate-950">{topic.topic}</strong>
          <span
            className={`text-xs font-bold ${topic.trendDirection === 'Down' ? 'text-rose-700' : topic.trendDirection === 'Stable' ? 'text-slate-500' : 'text-emerald-700'}`}
          >
            {trendText(topic)}
          </span>
          <span className="text-xs text-slate-400">vs previous period</span>
        </span>
        <span className="mt-1 block text-sm text-slate-500">
          {topic.totalInteractions} interactions · {topic.uniqueParticipants}{' '}
          {topic.uniqueParticipants === 1 ? 'participant' : 'participants'}
        </span>
        <span className="mt-1 block text-sm text-slate-600">
          {coverageLabel(topic.coverageStatus)} ·{' '}
          {topic.availableEmployees > 0
            ? `${topic.availableEmployees} ${topic.availableEmployees === 1 ? 'expert' : 'experts'} available`
            : 'No approved availability'}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-0.5" />
    </button>
  );
  const primaryChannel = selected
    ? [
        { label: 'Posts', value: selected.posts },
        { label: 'Comments', value: selected.comments },
        { label: 'Reactions', value: selected.reactions },
        { label: 'Group Chat', value: selected.groupChatMessages },
        { label: 'List Chat', value: selected.listChatMessages },
      ].sort((a, b) => b.value - a.value)[0]
    : null;
  const explanation = selected
    ? `${selected.topic} generated ${selected.currentInteractions} interactions during the current period compared with ${selected.previousInteractions} during the previous period${selected.trendPercentage == null ? '.' : `, representing a ${Math.abs(selected.trendPercentage)}% ${selected.trendPercentage >= 0 ? 'increase' : 'decrease'}.`}${primaryChannel && primaryChannel.value > 0 ? ` Most activity occurred in ${primaryChannel.label}, which contributed ${Math.round((primaryChannel.value * 100) / selected.currentInteractions)}% of discussion activity.` : ''}`
    : '';
  return (
    <Panel title="Trending Discussion Topics">
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        {topics.length ? (
          topics.slice(0, 5).map(renderRow)
        ) : (
          <p className="p-6 text-center text-sm text-slate-500">
            No classified discussion topics were detected for this period.
          </p>
        )}
      </div>
      {topics.length > 5 && (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="rounded-md px-2 py-1 text-sm font-bold text-brand-primary hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            View All Topics →
          </button>
        </div>
      )}
      {showAll && (
        <AccessibleDialog
          isOpen={showAll}
          onClose={() => setShowAll(false)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
          ariaLabel="All trending topics"
        >
          <div className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-2xl bg-white p-5">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xl font-bold">All Trending Topics</h3>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                aria-label="Close all topics"
                data-dialog-initial-focus
                className="rounded-lg p-2 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {topics.map(renderRow)}
          </div>
        </AccessibleDialog>
      )}
      {selected && (
        <AccessibleDialog
          isOpen={Boolean(selected)}
          onClose={() => setSelected(null)}
          className="fixed inset-0 z-50 bg-slate-950/40"
          ariaLabelledBy="topic-drawer-title"
        >
          <aside className="ml-auto h-full w-full overflow-y-auto bg-white shadow-2xl sm:max-w-xl">
            <header className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-200 bg-white p-5">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-brand-primary">
                  Topic Insights
                </p>
                <h3
                  id="topic-drawer-title"
                  className="mt-1 text-2xl font-bold text-slate-950"
                >
                  {selected.topic}
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  {statusOf(selected)} · {trendText(selected)} · Compared with
                  previous {data.period.days} days
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Close topic insights"
                data-dialog-initial-focus
                className="rounded-lg p-2 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </header>
            <div className="space-y-5 p-5">
              <section>
                <h4 className="font-bold text-slate-900">Trend Summary</h4>
                <dl className="mt-3 grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-sm">
                  <div>
                    <dt className="text-slate-500">Current Period</dt>
                    <dd className="text-xl font-bold">
                      {selected.currentInteractions}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Previous Period</dt>
                    <dd className="text-xl font-bold">
                      {selected.previousInteractions}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Change</dt>
                    <dd className="font-bold">
                      {selected.trendPercentage == null
                        ? 'NEW'
                        : `${selected.trendPercentage > 0 ? '+' : ''}${selected.trendPercentage}%`}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-slate-500">Status</dt>
                    <dd className="font-bold">{statusOf(selected)}</dd>
                  </div>
                </dl>
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {explanation}
                </p>
              </section>
              <section>
                <h4 className="font-bold text-slate-900">Activity Breakdown</h4>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {[
                    ['Total', selected.currentInteractions],
                    ['Posts', selected.posts],
                    ['Comments', selected.comments],
                    ['Reactions', selected.reactions],
                    ['Group Chat', selected.groupChatMessages],
                    ['List Chat', selected.listChatMessages],
                    ['Participants', selected.uniqueParticipants],
                  ].map(([label, value]) => (
                    <div
                      key={String(label)}
                      className="rounded-lg border border-slate-200 p-3"
                    >
                      <strong className="block text-lg">{value}</strong>
                      <span className="text-xs text-slate-500">{label}</span>
                    </div>
                  ))}
                </div>
              </section>
              <section>
                <h4 className="font-bold text-slate-900">
                  Primary Discussion Channel
                </h4>
                {primaryChannel && primaryChannel.value > 0 ? (
                  <div className="mt-3 rounded-xl bg-slate-50 p-4">
                    <p className="text-lg font-bold text-slate-950">
                      {primaryChannel.label}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {primaryChannel.value} of {selected.currentInteractions}{' '}
                      interactions ·{' '}
                      {Math.round(
                        (primaryChannel.value * 100) /
                          selected.currentInteractions
                      )}
                      % of discussion activity
                    </p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">
                    No discussion activity is available for this topic.
                  </p>
                )}
              </section>
              <section>
                <h4 className="font-bold text-slate-900">
                  Where People Are Discussing It
                </h4>
                {selected.discussionSources.length ? (
                  <div className="mt-2 space-y-2">
                    {selected.discussionSources.map((source) => (
                      <button
                        key={`${source.sourceType}-${source.sourceId}`}
                        type="button"
                        disabled={!source.canNavigate}
                        onClick={() =>
                          source.canNavigate &&
                          source.sourceType === 'Group Chat' &&
                          onViewGroup(source.sourceId)
                        }
                        className="flex w-full items-center justify-between gap-3 rounded-xl border border-slate-200 p-3 text-left hover:bg-slate-50 disabled:cursor-default"
                      >
                        <span>
                          <strong className="block text-sm text-slate-900">
                            {source.name}
                          </strong>
                          <span className="text-xs text-slate-500">
                            {source.sourceType}
                          </span>
                        </span>
                        <span className="text-right text-xs text-slate-600">
                          {source.messageCount} messages
                          <br />
                          {source.uniqueParticipants} participants
                        </span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="mt-2 space-y-1 text-sm text-slate-500">
                    <p>No related group chat activity was found.</p>
                    <p>No related list chat activity was found.</p>
                  </div>
                )}
              </section>
              <section className="rounded-xl bg-blue-50 p-4">
                <h4 className="font-bold text-blue-950">Internal Expertise</h4>
                {selected.employeesWithSkill > 0 ? (
                  <div className="mt-2 text-sm leading-6 text-blue-900">
                    <p>
                      {selected.employeesWithSkill} employees have matching
                      skills
                    </p>
                    {selected.availableEmployees > 0 ? (
                      <>
                        <p>{selected.availableEmployees} currently available</p>
                        <p>{selected.availableHours} approved hrs/week</p>
                      </>
                    ) : (
                      <p>No approved expert availability currently.</p>
                    )}
                    <span className="mt-2 inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-bold">
                      {coverageLabel(selected.coverageStatus)}
                    </span>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-blue-900">
                    No internal skill mapping is available for this topic.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => onFindExperts(selected.topic)}
                  className="mt-4 rounded-lg bg-brand-primary px-4 py-2 text-sm font-bold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
                >
                  Find Experts
                </button>
              </section>
              {selected.relatedHashtags.length > 0 && (
                <section>
                  <h4 className="font-bold text-slate-900">Related Hashtags</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.relatedHashtags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              {selected.relatedTopics.length > 0 && (
                <section>
                  <h4 className="font-bold text-slate-900">Related Topics</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {selected.relatedTopics.map((topic) => (
                      <span
                        key={topic}
                        className="rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700"
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </section>
              )}
              <section>
                <h4 className="font-bold text-slate-900">Recent Discussions</h4>
                {selected.recentDiscussions.length ? (
                  <div className="mt-2 space-y-2">
                    {selected.recentDiscussions.map((item) => (
                      <article
                        key={item.postId}
                        className="rounded-xl border border-slate-200 p-3"
                      >
                        <p className="line-clamp-2 text-sm font-semibold text-slate-800">
                          {item.text}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">
                          {new Date(item.createdAtUtc).toLocaleDateString()} ·{' '}
                          {item.postType} · {item.commentCount} comments ·{' '}
                          {item.reactionCount} reactions
                        </p>
                        <button
                          type="button"
                          onClick={() => onViewPost(item.postId)}
                          className="mt-2 text-sm font-bold text-brand-primary"
                        >
                          View Post →
                        </button>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-slate-500">
                    No recent discussions are available for this topic.
                  </p>
                )}
              </section>
            </div>
          </aside>
        </AccessibleDialog>
      )}
    </Panel>
  );
}

function TopicActivityOverview({
  data,
}: {
  data: WorkforceIntelligenceDashboard;
}) {
  const topics = data.discussionTopics
    .filter(
      (topic) => !['other', 'unclassified'].includes(topic.topic.toLowerCase())
    )
    .slice(0, 7);
  const colors = {
    posts: '#2563eb',
    comments: '#7c3aed',
    group: '#0d9488',
    list: '#0891b2',
  };
  return (
    <Panel title="Discussion Activity by Topic">
      <div className="mb-4 flex flex-wrap gap-4 text-xs font-semibold text-slate-600">
        {[
          ['Posts', colors.posts],
          ['Comments', colors.comments],
          ['Group Chat', colors.group],
          ['List Chat', colors.list],
        ].map(([label, color]) => (
          <span key={label} className="inline-flex items-center gap-1.5">
            <span
              className="h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: color }}
            />
            {label}
          </span>
        ))}
      </div>
      {topics.length ? (
        <div className="space-y-3">
          {topics.map((topic) => {
            const represented =
              topic.posts +
              topic.comments +
              topic.groupChatMessages +
              topic.listChatMessages;
            const segments = [
              { key: 'posts', value: topic.posts, color: colors.posts },
              {
                key: 'comments',
                value: topic.comments,
                color: colors.comments,
              },
              {
                key: 'group',
                value: topic.groupChatMessages,
                color: colors.group,
              },
              {
                key: 'list',
                value: topic.listChatMessages,
                color: colors.list,
              },
            ];
            return (
              <div
                key={topic.topic}
                className="grid gap-1 sm:grid-cols-[12rem_1fr_5rem] sm:items-center sm:gap-3"
              >
                <span
                  className="truncate text-sm font-semibold text-slate-700"
                  title={topic.topic}
                >
                  {topic.topic}
                </span>
                <div
                  className="flex h-6 overflow-hidden rounded-full bg-slate-100"
                  aria-label={`${topic.topic}: ${represented} source interactions`}
                >
                  {segments.map(
                    (segment) =>
                      segment.value > 0 && (
                        <span
                          key={segment.key}
                          title={`${segment.key}: ${segment.value}`}
                          className="h-full transition-[width] duration-500"
                          style={{
                            width: `${(segment.value * 100) / Math.max(1, represented)}%`,
                            backgroundColor: segment.color,
                          }}
                        />
                      )
                  )}
                </div>
                <span className="text-right text-sm font-bold text-slate-600">
                  {topic.currentInteractions}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
          No discussion activity was found for the selected period.
        </p>
      )}
    </Panel>
  );
}

function AttentionMovements({
  data,
}: {
  data: WorkforceIntelligenceDashboard;
}) {
  const classified = data.discussionTopics.filter(
    (topic) => !['other', 'unclassified'].includes(topic.topic.toLowerCase())
  );
  const gaining = classified
    .filter(
      (topic) =>
        topic.trendDirection === 'New' || (topic.trendPercentage ?? 0) > 0
    )
    .sort(
      (a, b) =>
        (b.trendPercentage ?? Number.MAX_VALUE) -
        (a.trendPercentage ?? Number.MAX_VALUE)
    )
    .slice(0, 5);
  const cooling = classified
    .filter((topic) => (topic.trendPercentage ?? 0) < 0)
    .sort((a, b) => (a.trendPercentage ?? 0) - (b.trendPercentage ?? 0))
    .slice(0, 5);
  const table = (items: typeof classified, empty: string) =>
    items.length ? (
      <div className="divide-y divide-slate-100">
        {items.map((topic) => (
          <div
            key={topic.topic}
            className="py-3 text-sm"
          >
            <strong
              className="mb-2 block truncate text-slate-800"
              title={topic.topic}
            >
              {topic.topic}
            </strong>
            <div className="grid grid-cols-3 gap-2">
              <span className="rounded-lg bg-slate-50 px-2 py-1.5 text-slate-500">
                <span className="block text-xs font-medium text-slate-400">
                  Current
                </span>
                <span className="font-semibold text-slate-700">{topic.currentInteractions}</span>
              </span>
              <span className="rounded-lg bg-slate-50 px-2 py-1.5 text-slate-500">
                <span className="block text-xs font-medium text-slate-400">
                  Previous
                </span>
                <span className="font-semibold text-slate-700">{topic.previousInteractions}</span>
              </span>
              <span
                className={`rounded-lg px-2 py-1.5 font-bold ${topic.trendDirection === 'New' || (topic.trendPercentage ?? 0) > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}
              >
                <span className="block text-xs font-medium opacity-70">
                  Change
                </span>
                <span className="block">
                  {topic.trendDirection === 'New'
                    ? 'NEW'
                    : `${(topic.trendPercentage ?? 0) > 0 ? '↑ +' : '↓ '}${topic.trendPercentage}%`}
                </span>
              </span>
            </div>
          </div>
        ))}
      </div>
    ) : (
      <p className="py-5 text-sm text-slate-500">{empty}</p>
    );
  return (
    <Panel title="Attention Movement">
      <div className="grid gap-5 lg:grid-cols-2">
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-emerald-700">
            Gaining Attention
          </h3>
          {table(gaining, 'No topics are gaining attention in this period.')}
        </section>
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wide text-rose-700">
            Cooling Discussions
          </h3>
          {table(cooling, 'No discussions are cooling in this period.')}
        </section>
      </div>
    </Panel>
  );
}

function DemandExpertiseQuadrant({
  data,
  onFindExperts,
}: {
  data: WorkforceIntelligenceDashboard;
  onFindExperts: (topic: string) => void;
}) {
  const topics = data.discussionTopics
    .filter(
      (topic) => !['other', 'unclassified'].includes(topic.topic.toLowerCase())
    )
    .slice(0, 12);
  const maxDemand = Math.max(
    1,
    ...topics.map((topic) => topic.currentInteractions)
  );
  const maxExpertise = Math.max(
    1,
    ...topics.map((topic) => topic.employeesWithSkill)
  );
  return (
    <Panel title="Conversation Demand vs Internal Expertise">
      <p className="mb-4 text-sm text-slate-500">
        Identify strong positions, capability gaps, underutilized expertise, and
        lower-priority topics from current operational data.
      </p>
      {topics.length ? (
        <div className="relative h-96 overflow-hidden rounded-xl border border-slate-200 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:50%_50%]">
          <span className="absolute left-3 top-3 text-xs font-bold text-slate-400">
            Underutilized Expertise
          </span>
          <span className="absolute right-3 top-3 text-xs font-bold text-emerald-700">
            Strong Position
          </span>
          <span className="absolute bottom-3 left-3 text-xs font-bold text-slate-400">
            Low Priority
          </span>
          <span className="absolute bottom-3 right-3 text-xs font-bold text-amber-700">
            Capability Gap
          </span>
          {topics.map((topic, index) => (
            <button
              key={topic.topic}
              type="button"
              onClick={() => onFindExperts(topic.topic)}
              title={`${topic.topic}: ${topic.currentInteractions} interactions, ${topic.employeesWithSkill} employees with matching skills`}
              className="absolute max-w-32 -translate-x-1/2 translate-y-1/2 truncate rounded-full border border-blue-200 bg-white px-2.5 py-1 text-xs font-bold text-brand-primary shadow-sm hover:z-10 hover:border-brand-primary focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              style={{
                left: `${8 + (topic.currentInteractions * 84) / maxDemand}%`,
                bottom: `${8 + (topic.employeesWithSkill * 84) / maxExpertise}%`,
              }}
            >
              {topic.topic}
            </button>
          ))}
        </div>
      ) : (
        <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
          No topic and expertise data is available for this period.
        </p>
      )}
      <div className="mt-2 flex justify-between text-xs font-semibold text-slate-500">
        <span>Low discussion</span>
        <span>Discussion / Interest → High</span>
      </div>
    </Panel>
  );
}

function HashtagAndGroupInsights({
  data,
}: {
  data: WorkforceIntelligenceDashboard;
}) {
  const [showAllHashtags, setShowAllHashtags] = useState(false);
  const hashtags = data.trendingHashtags ?? [];
  const renderHashtag = (tag: (typeof hashtags)[number], index: number) => {
    const status =
      tag.trendDirection === 'New'
        ? 'NEW'
        : tag.trendDirection === 'Down'
          ? '↓ Declining'
          : tag.trendDirection === 'Stable'
            ? '→ Stable'
            : '↑ Trending';
    const change =
      tag.trendPercentage == null
        ? 'New'
        : `${tag.trendPercentage > 0 ? '+' : ''}${tag.trendPercentage}%`;
    return (
      <div
        key={tag.tag}
        tabIndex={0}
        title={`Current Period: ${tag.usageCount}; Previous Period: ${tag.previousUsageCount}; Change: ${change}`}
        className="grid grid-cols-[2rem_1fr_auto_auto] items-center gap-2 rounded-lg px-2 py-2.5 text-sm hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
      >
        <span className="font-bold text-slate-400">{index + 1}.</span>
        <span className="min-w-0 truncate font-bold text-slate-800">
          #{tag.tag}
        </span>
        <span className="whitespace-nowrap text-slate-500">
          {tag.usageCount} {tag.usageCount === 1 ? 'mention' : 'mentions'}
        </span>
        <span
          className={`whitespace-nowrap rounded-full px-2 py-1 text-xs font-bold ${tag.trendDirection === 'Down' ? 'bg-rose-50 text-rose-700' : tag.trendDirection === 'Stable' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-50 text-emerald-700'}`}
        >
          {status}
        </span>
      </div>
    );
  };
  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <Panel title="Trending Hashtags">
        <div>
          {hashtags.length ? (
            hashtags.slice(0, 8).map(renderHashtag)
          ) : (
            <p className="rounded-lg bg-slate-50 p-6 text-center text-sm text-slate-500">
              No hashtags were mentioned during this period.
            </p>
          )}
        </div>
        {hashtags.length > 8 && (
          <div className="mt-3 text-right">
            <button
              type="button"
              onClick={() => setShowAllHashtags(true)}
              className="text-sm font-bold text-brand-primary"
            >
              View All Hashtags →
            </button>
          </div>
        )}
        {showAllHashtags && (
          <AccessibleDialog
            isOpen={showAllHashtags}
            onClose={() => setShowAllHashtags(false)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4"
            ariaLabel="All trending hashtags"
          >
            <div className="w-full max-w-2xl rounded-2xl bg-white p-5">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xl font-bold">All Trending Hashtags</h3>
                <button
                  type="button"
                  onClick={() => setShowAllHashtags(false)}
                  aria-label="Close all hashtags"
                  data-dialog-initial-focus
                  className="rounded-lg p-2 hover:bg-slate-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {hashtags.map(renderHashtag)}
            </div>
          </AccessibleDialog>
        )}
      </Panel>
      <Panel title="Most Active Public Groups">
        {data.activeDiscussionGroups.length ? (
          <div className="space-y-3">
            {data.activeDiscussionGroups.map((group, index) => (
              <article
                key={group.groupId}
                className="rounded-xl bg-slate-50 p-4"
              >
                <div className="flex justify-between gap-3">
                  <h3 className="font-bold text-slate-900">
                    #{index + 1} {group.groupName}
                  </h3>
                  <span className="text-sm font-semibold text-brand-primary">
                    {group.messages + group.reactions} interactions
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {group.messages} discussions · {group.reactions} reactions ·{' '}
                  {group.uniqueParticipants} active participants
                </p>
              </article>
            ))}
          </div>
        ) : data.publicGroupAnalysisStatus ===
          'PublicVisibilityNotConfigured' ? (
          <div className="rounded-xl bg-slate-50 p-6 text-center">
            <p className="font-bold text-slate-800">No public groups found.</p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              The current group model does not identify any group as public, so
              membership-only discussions are excluded from workforce analytics.
            </p>
          </div>
        ) : (
          <div className="rounded-xl bg-slate-50 p-6 text-center">
            <p className="font-bold text-slate-800">
              No public group activity during this period.
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Try selecting a longer time range.
            </p>
          </div>
        )}
      </Panel>
    </div>
  );
}

export default function WorkforceIntelligencePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const query = useQuery({
    queryKey: ['workforce-intelligence', days],
    queryFn: () => getWorkforceIntelligence(days),
    staleTime: 0,
    refetchOnMount: 'always',
  });
  if (query.isLoading)
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-32 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  if (query.isError)
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-8 text-center">
        <AlertTriangle className="mx-auto h-8 w-8 text-red-600" />
        <h2 className="mt-3 font-bold text-red-900">
          Unable to load workforce intelligence
        </h2>
        <p className="mt-1 text-sm text-red-700">
          {query.error instanceof Error
            ? query.error.message
            : 'Please try again.'}
        </p>
        <button
          type="button"
          onClick={() => query.refetch()}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white"
        >
          <RefreshCw className="h-4 w-4" />
          Retry
        </button>
      </div>
    );
  const data = query.data;
  if (!data) return null;
  const overview = data.overview;
  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-8">
      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-primary">
            Management analytics
          </p>
          <h1 className="mt-1 text-3xl font-bold text-slate-950">
            Workforce Intelligence &amp; Insights
          </h1>
          <p className="mt-2 max-w-4xl text-slate-600">
            Understand what your organization is discussing, where conversations
            happen, and the internal expertise available.
          </p>
        </div>
        <label className="text-sm font-semibold text-slate-700">
          Analysis period
          <select
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            className="ml-2 rounded-lg border border-slate-300 bg-white px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
            style={{ backgroundColor: theme.surface, borderColor: theme.border, color: theme.text }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </select>
        </label>
      </header>
      <section aria-labelledby="workforce-overview">
        <h2 id="workforce-overview" className="sr-only">
          Workforce Overview
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label="Active Employees"
            value={overview.activeEmployees.toLocaleString()}
            detail={`${overview.completedProfiles} completed profiles`}
            icon={UsersRound}
          />
          <MetricCard
            label="Skills Represented"
            value={overview.totalSkillsRepresented.toLocaleString()}
            detail={`${overview.totalSpecialties} IT specialties`}
            icon={BriefcaseBusiness}
          />
          <MetricCard
            label="Employees Available"
            value={overview.employeesWithApprovedAvailability.toLocaleString()}
            detail="Approved current availability only"
            icon={Clock3}
          />
          <MetricCard
            label="Available Internal Capacity"
            value={`${overview.totalApprovedAvailableHours.toLocaleString()} hrs`}
            detail={`${overview.averageAvailableHours} average hours per available employee`}
            icon={BarChart3}
          />
        </div>
      </section>
      <WorkforceCapabilityOverview data={data} />
      <TrendingTopics
        data={data}
        onFindExperts={(topic) =>
          navigate('/ask-isentryx-ai', {
            state: {
              query: `Find experts in ${topic} who are currently available`,
            },
          })
        }
        onViewPost={(postId) => navigate(`/posts/${postId}`)}
        onViewGroup={(groupId) => navigate(`/groups/${groupId}`)}
      />
      <TopicActivityOverview data={data} />
      <AttentionMovements data={data} />
      {/* <DemandExpertiseQuadrant
        data={data}
        onFindExperts={(topic) =>
          navigate('/ask-isentryx-ai', {
            state: {
              query: `Find experts in ${topic} who are currently available`,
            },
          })
        }
      /> */}
      <HashtagAndGroupInsights data={data} />
      <Panel title="AI-Powered Workforce Insights">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.insights.length ? (
            data.insights.map((insight) => (
              <article
                key={`${insight.category}-${insight.title}`}
                className={`rounded-xl border p-4 ${insight.severity === 'Attention' ? 'border-amber-200 bg-amber-50' : insight.severity === 'Opportunity' ? 'border-emerald-200 bg-emerald-50' : 'border-blue-200 bg-blue-50'}`}
                style={theme.isDark ? {
                  backgroundColor: theme.surfaceHover,
                  borderColor: theme.border,
                  color: theme.text,
                } : undefined}
              >
                <div className="flex items-center gap-2">
                  <Lightbulb className="h-4 w-4" />
                  <span className="text-xs font-bold uppercase tracking-wide">
                    {insight.category} · {insight.severity}
                  </span>
                </div>
                <h3 className="mt-2 font-bold text-slate-950">
                  {insight.title}
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-700">
                  {insight.description}
                </p>
              </article>
            ))
          ) : (
            <p className="text-sm text-slate-500">
              No workforce data is currently available for this metric.
            </p>
          )}
        </div>
      </Panel>
      <Panel title="Collaboration & Engagement">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            label="Active Groups"
            value={data.collaboration.activeGroups}
            detail={`${data.collaboration.activeGroupMemberships} active memberships`}
            icon={Network}
          />
          <MetricCard
            label="Posts Created"
            value={data.collaboration.postsCreated}
            detail={`${data.collaboration.employeesWhoPosted} contributors`}
            icon={BriefcaseBusiness}
          />
          <MetricCard
            label="Comments"
            value={data.collaboration.commentsCreated}
            detail={`${data.collaboration.employeesWhoCommented} contributors`}
            icon={UsersRound}
          />
          <MetricCard
            label="Participation Rate"
            value={`${data.collaboration.engagementRate}%`}
            detail="Neutral activity participation measure"
            icon={BarChart3}
          />
        </div>
      </Panel>
      <p className="text-right text-xs text-slate-400">
        Generated {new Date(data.generatedAtUtc).toLocaleString()} · Current
        distributions only
      </p>
    </div>
  );
}
